import $api from "../queries/openapi";
import useGetShopSettings from "@/lib/queries/useGetShopSettings";
import QboLogoIcon from "~icons/local/qbo-ball-logo";
import { useParams } from "react-router";
import { QuoteRevision } from "@/lib/queries/types";
import { useQuery } from "@tanstack/react-query";
import { createFinalURL, createQuerySerializer } from "openapi-fetch";
import { useState } from "react";

export default function AccountingSystemDisplay({
  quoteRevision,
}: {
  quoteRevision: QuoteRevision;
}) {
  const { teamSlug } = useParams();
  const { data: shop } = useGetShopSettings(teamSlug);
  const hasAccountingSystem = !!shop?.accounting_system_name;

  const [accountingSystemUnavailable, setAccountingSystemUnavailable] =
    useState(false);

  const {
    data: accountingSystemQuoteRevision,
    isLoading,
    error: accountingSystemQuoteRevisionError,
  } = useQuery({
    ...$api.queryOptions(
      "get",
      "/api/teams/{team_slug}/accounting_system/quote_revisions/{id}",
      {
        params: {
          path: {
            team_slug: teamSlug!,
            id: quoteRevision.id,
          },
        },
      },
      {
        enabled: hasAccountingSystem,
        refetchOnWindowFocus: false,
      },
    ),
    queryFn: async ({ queryKey }) => {
      const [, path, options] = queryKey;
      const url = createFinalURL(path, {
        params: options.params,
        baseUrl: "",
        querySerializer: createQuerySerializer(),
      });
      const res = await window.fetch(url);
      if (res.status !== 200) {
        if (res.status === 503) {
          setAccountingSystemUnavailable(true);
        }
        throw new Error(
          `Error fetching accounting system quote revision: ${res.status} ${res.statusText}`,
        );
      }
      return await res.json();
    },
  });

  if (!hasAccountingSystem) {
    return null;
  }

  if (
    !isLoading &&
    !accountingSystemQuoteRevision &&
    !accountingSystemUnavailable
  ) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <a
        className="rounded-sm cursor-pointer hover:bg-blue-100 py-1 flex items-center gap-2"
        {...(accountingSystemQuoteRevision?.link
          ? { href: accountingSystemQuoteRevision.link, target: "_blank" }
          : {})}
      >
        <QboLogoIcon className="size-7" />
        <div
          className="text-sm truncate"
          title={accountingSystemQuoteRevision?.name}
        >
          {accountingSystemQuoteRevisionError
            ? "Error: Offline"
            : isLoading
              ? "Loading..."
              : accountingSystemQuoteRevision?.name}
        </div>
      </a>
    </div>
  );
}
